import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { streamingServiceProviderInfoService } from '../../../../services/streamingServiceProviderInfo';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { useUserSelection } from '../../../../contexts/UserSelectionContext';
const logger = createLogger('StreamingServiceProviderData');

type RouteParams = {
  taskId: number;
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const StreamingServiceProviderDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const { targetUserId, viewOnly } = route.params || {};
  // K5: a successor sees the owner's logins once the death is verified.
  const { isDeathVerifiedForSelectedUser } = useUserSelection();
  const [streamingProviderData, setStreamingProviderData] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showUsername, setShowUsername] = useState<{ [key: string]: boolean }>({});
  const [showPassword, setShowPassword] = useState<{ [key: string]: boolean }>({});
  const [showAccountNumber, setShowAccountNumber] = useState<{ [key: string]: boolean }>({});

  useEffect(() => {
    loadStreamingProviderData();
  }, []);

  const loadStreamingProviderData = async () => {
    try {
      setLoading(true);
      const data = await streamingServiceProviderInfoService.getAllStreamingServiceProviderInfo(targetUserId);
      
      if (data && data.length > 0) {
        // Skip loading portal credentials in viewOnly mode - they are protected until EOL
        if (!viewOnly || isDeathVerifiedForSelectedUser) {
          // Load portal credentials for each streaming provider
          const updatedData = await Promise.all(
            data.map(async (streamingProvider) => {
              try {
                logger.debug('[Portal Debug] Attempting to load portal credentials for streaming provider ID:', streamingProvider.id);
                const portalData = await vaultService.getPortalCredentials(streamingProvider.id, 'streaming-service-provider');
                logger.debug('[Portal Debug] Portal data retrieved for streaming provider:', streamingProvider.id, portalData);
                if (portalData) {
                  streamingProvider.username = portalData.username;
                  streamingProvider.password = portalData.password;
                  streamingProvider.accountNumber = portalData.accountNumber;
                  logger.debug('[Portal Debug] Assigned credentials to streaming provider data');
                } else {
                  logger.debug('[Portal Debug] No portal data returned for streaming provider:', streamingProvider.id);
                }
              } catch (vaultError) {
                logger.debug('[Portal Debug] Error loading portal data for streaming provider:', streamingProvider.id, vaultError);
              }
              return streamingProvider;
            })
          );

          logger.debug('[Data Debug] Final streaming provider data being set:', updatedData.length, 'providers');
          logger.debug('[Data Debug] First provider account number:', updatedData[0]?.accountNumber);
          setStreamingProviderData(updatedData);
        } else {
          setStreamingProviderData(data);
        }
      }
    } catch (error) {
      logger.error('Error loading streaming provider data:', error);
      Alert.alert('Error', 'Failed to load streaming service provider information');
    } finally {
      setLoading(false);
    }
  };

  const formatPhoneNumber = (phone: string) => {
    if (!phone) return '';
    const cleaned = phone.replace(/\D/g, '');
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }
    return phone;
  };

  const formatCurrency = (amount: string | number) => {
    if (!amount) return '';
    if (typeof amount === 'number') {
      return `$${amount.toFixed(2)}`;
    }
    return amount.toString();
  };

  const handleEditStreamingProvider = (streamingProvider: any) => {
    navigation.navigate('StreamingServiceProviderInput', { 
      streamingServiceProviderId: streamingProvider.id, 
      isEdit: true 
    });
  };

  const handleDeleteStreamingProvider = async (streamingProvider: any) => {
    Alert.alert('Delete', 'Are you sure you want to delete this streaming service provider information?', [
      { text: 'Cancel', style: 'cancel' },
      { 
        text: 'Delete', 
        style: 'destructive', 
        onPress: async () => {
          try {
            await streamingServiceProviderInfoService.deleteStreamingServiceProviderInfo(streamingProvider.id);
            
            // Reload data after deletion
            await loadStreamingProviderData();
            
            Alert.alert('Deleted', 'Streaming service provider information deleted.');
          } catch (e) {
            logger.error('Delete streaming provider info failed:', e);
            Alert.alert('Error', 'Failed to delete streaming service provider information');
          }
        } 
      },
    ]);
  };

  const handleAddStreamingProvider = () => {
    navigation.navigate('StreamingServiceProviderInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Streaming Service Provider Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading streaming service provider information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!streamingProviderData || streamingProviderData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Streaming Service Provider Information</Text>

        <View style={styles.whiteContainer}>
          {viewOnly && (
            <View style={styles.viewOnlyBanner}>
              <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
            </View>
          )}
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No streaming service provider information found</Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddStreamingProvider}
              >
                <Text style={styles.addButtonText}>Add Streaming Service Provider Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Streaming Service Provider Information</Text>

      <View style={styles.whiteContainer}>
        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}
        <ScrollView style={styles.scrollView}>
          {streamingProviderData.map((streamingProvider, index) => (
            <View key={streamingProvider.id || index} style={styles.streamingProviderCard}>
              <View style={styles.streamingProviderHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.streamingProviderName}>
                    {streamingProvider.serviceName || 'Unknown Provider'}
                  </Text>
                  <Text style={styles.streamingProviderType}>
                    {streamingProvider.subscriptionPlan || 'Not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editStreamingProviderButton}
                      onPress={() => handleEditStreamingProvider(streamingProvider)}
                    >
                      <Text style={styles.editStreamingProviderButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteStreamingProviderButton}
                      onPress={() => handleDeleteStreamingProvider(streamingProvider)}
                    >
                      <Text style={styles.deleteStreamingProviderButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>
              
              <InfoItem label="Service Name" value={streamingProvider.serviceName || ''} />
              
              {streamingProvider.accountNumber && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Account Number 🔒</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showAccountNumber[streamingProvider.id] ? streamingProvider.accountNumber : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowAccountNumber(prev => ({ ...prev, [streamingProvider.id]: !prev[streamingProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showAccountNumber[streamingProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              <InfoItem label="Subscription Plan" value={streamingProvider.subscriptionPlan || ''} />
              <InfoItem label="Monthly Cost" value={formatCurrency(streamingProvider.monthlyCost) || ''} />
              <InfoItem label="Billing Cycle Date" value={streamingProvider.billingCycleDate || ''} />
              <InfoItem label="Account Email" value={streamingProvider.accountEmail || ''} />
              <InfoItem label="Website" value={streamingProvider.website || ''} />
              <InfoItem label="Autopay Enabled" value={streamingProvider.autopayEnabled ? 'Yes' : 'No'} />
              <InfoItem label="Payment Method" value={streamingProvider.paymentMethod || ''} />
              <InfoItem label="Family Sharing" value={streamingProvider.familySharing ? 'Yes' : 'No'} />
              <InfoItem label="Max Profiles" value={streamingProvider.maxProfiles?.toString() || ''} />
              
              {streamingProvider.username && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Username</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showUsername[streamingProvider.id] ? streamingProvider.username : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowUsername(prev => ({ ...prev, [streamingProvider.id]: !prev[streamingProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showUsername[streamingProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              {streamingProvider.password && (
                <View style={styles.infoItem}>
                  <Text style={styles.label}>Portal Password</Text>
                  <View style={styles.valueRow}>
                    <Text style={styles.value}>{showPassword[streamingProvider.id] ? streamingProvider.password : '••••••••••'}</Text>
                    <TouchableOpacity onPress={() => setShowPassword(prev => ({ ...prev, [streamingProvider.id]: !prev[streamingProvider.id] }))} style={styles.eyeButton}>
                      <Image
                        source={showPassword[streamingProvider.id] ? require('../../../../assets/icons/Eye.png') : require('../../../../assets/icons/Eye-closed.png')}
                        style={styles.eyeIcon}
                      />
                    </TouchableOpacity>
                  </View>
                  <View style={styles.separator} />
                </View>
              )}
              
              <InfoItem label="Notes" value={streamingProvider.notes || ''} />
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherStreamingProviderButton}
                onPress={handleAddStreamingProvider}
              >
                <Text style={styles.addAnotherStreamingProviderButtonText}>Add Another Streaming Service Provider</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('PropertyChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  streamingProviderCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  streamingProviderHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  streamingProviderName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  streamingProviderType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editStreamingProviderButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editStreamingProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteStreamingProviderButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteStreamingProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  valueRow: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  eyeButton: {
    padding: ms(5),
    marginLeft: ms(10),
  },
  eyeIcon: {
    width: s(16),
    height: s(16),
    tintColor: '#6B7280',
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherStreamingProviderButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherStreamingProviderButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
